import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  CalendarDays,
  Crown,
  Files,
  Flag,
  Plus,
  Pin,
  Send,
  Shield,
  Tags,
  Target,
  TrendingUp,
  Upload,
  Users,
} from "lucide-react";
import { AuthContext } from "./AuthContext";
import { API_BASE_URL } from "./api";
import {
  Button,
  Field,
  MetricCard,
  SectionHeader,
  SelectField,
  StatusPill,
  SurfaceCard,
  TextareaField,
} from "./components/designSystem";
import { DualCurrencyValue, formatNaira, formatUsd, getTradeNgnProfit, getTradeUsdProfit, useExchangeRate } from "./utils/currencyDisplay";

function getId(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "object" && value._id) {
    return String(value._id);
  }

  return String(value);
}

function getUserLabel(user) {
  if (!user) {
    return "Unknown user";
  }

  if (typeof user === "string") {
    return user;
  }

  return user.username || user.name || user.email || getId(user) || "Unknown user";
}

function getUserEmail(user) {
  if (!user || typeof user === "string") {
    return "-";
  }

  return user.email || "-";
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString();
}

function activitySummary(activity) {
  const actor = getUserLabel(activity.user);

  if (activity.type === "trade") {
    return `${actor} recorded a trade${activity.details?.asset ? ` on ${activity.details.asset}` : ""}.`;
  }

  if (activity.type === "message") {
    return `${actor} sent a message${activity.details?.content ? `: ${activity.details.content}` : "."}`;
  }

  if (activity.type === "member-join") {
    return `${actor} joined the team.`;
  }

  if (activity.type === "member-leave") {
    return `${actor} left the team.`;
  }

  return `${actor} triggered a team event.`;
}

function TeamManagement({ isEnterprise }) {
  const { token, user } = useContext(AuthContext);
  const { usdToNgnRate } = useExchangeRate();
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [feedback, setFeedback] = useState({ tone: "neutral", text: "" });
  const [reloadKey, setReloadKey] = useState(0);

  const [createTeamName, setCreateTeamName] = useState("");
  const [createTeamMembers, setCreateTeamMembers] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [goalForm, setGoalForm] = useState({ title: "", description: "", target: "" });
  const [goalProgressDrafts, setGoalProgressDrafts] = useState({});
  const [calendarForm, setCalendarForm] = useState({ title: "", description: "", start: "", end: "", type: "event" });
  const [tagForm, setTagForm] = useState({ name: "", color: "#0ea5e9" });
  const [uploadFile, setUploadFile] = useState(null);

  const [teamTrades, setTeamTrades] = useState([]);
  const [teamAnalytics, setTeamAnalytics] = useState(null);
  const [teamMessages, setTeamMessages] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [teamGoals, setTeamGoals] = useState([]);
  const [teamCalendar, setTeamCalendar] = useState([]);
  const [teamFiles, setTeamFiles] = useState([]);
  const [teamTags, setTeamTags] = useState([]);

  const selectedTeam = teams.find((team) => getId(team._id) === selectedTeamId) || null;
  const currentUserId = getId(user?._id || localStorage.getItem("userId"));

  const ownerId = selectedTeam ? getId(selectedTeam.owner) : "";
  const memberRole = selectedTeam?.members?.find((member) => getId(member.user) === currentUserId)?.role || null;
  const myRole = ownerId === currentUserId ? "owner" : memberRole;
  const canManageRoles = myRole === "owner" || myRole === "admin";
  const canRemoveMembers = myRole === "owner";
  const canManageWorkspace = myRole === "owner" || myRole === "admin";

  const request = useCallback(async (path, options = {}) => {
    const headers = {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };

    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
    });

    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      throw new Error(data?.error || "Request failed.");
    }

    return data;
  }, [token]);

  useEffect(() => {
    let active = true;

    async function loadTeams() {
      setLoadingTeams(true);

      try {
        const data = await request("/api/teams");
        if (!active) {
          return;
        }

        const nextTeams = Array.isArray(data?.teams) ? data.teams : [];
        setTeams(nextTeams);
        setSelectedTeamId((current) => {
          if (current && nextTeams.some((team) => getId(team._id) === current)) {
            return current;
          }
          return nextTeams[0]?._id ? getId(nextTeams[0]._id) : "";
        });
      } catch (error) {
        if (active) {
          setTeams([]);
          setSelectedTeamId("");
          setFeedback({ tone: "danger", text: error.message || "Failed to load teams." });
        }
      } finally {
        if (active) {
          setLoadingTeams(false);
        }
      }
    }

    if (token) {
      loadTeams();
    }

    return () => {
      active = false;
    };
  }, [request, token, reloadKey]);

  useEffect(() => {
    let active = true;

    async function loadWorkspace() {
      if (!selectedTeamId) {
        setTeamTrades([]);
        setTeamAnalytics(null);
        setTeamMessages([]);
        setPinnedMessages([]);
        setActivityFeed([]);
        setLeaderboard([]);
        setTeamGoals([]);
        setTeamCalendar([]);
        setTeamFiles([]);
        setTeamTags([]);
        return;
      }

      setLoadingWorkspace(true);

      try {
        const [trades, analytics, messages, pinned, activity, board, goals, calendar, files, tags] = await Promise.all([
          request(`/api/team/${selectedTeamId}/trades`),
          request(`/api/team/${selectedTeamId}/analytics`),
          request(`/api/team/${selectedTeamId}/messages`),
          request(`/api/team/${selectedTeamId}/pinned-messages`),
          request(`/api/team/${selectedTeamId}/activity`),
          request(`/api/team/${selectedTeamId}/leaderboard`),
          request(`/api/team/${selectedTeamId}/goals`),
          request(`/api/team/${selectedTeamId}/calendar`),
          request(`/api/team/${selectedTeamId}/files`),
          request(`/api/team/${selectedTeamId}/tags`),
        ]);

        if (!active) {
          return;
        }

        setTeamTrades(Array.isArray(trades) ? trades : []);
        setTeamAnalytics(analytics && typeof analytics === "object" ? analytics : null);
        setTeamMessages(Array.isArray(messages) ? messages : []);
        setPinnedMessages(Array.isArray(pinned) ? pinned : []);
        setActivityFeed(Array.isArray(activity) ? activity : []);
        setLeaderboard(Array.isArray(board) ? board : []);
        setTeamGoals(Array.isArray(goals) ? goals : []);
        setGoalProgressDrafts(
          (Array.isArray(goals) ? goals : []).reduce((result, goal, index) => {
            result[index] = goal.progress ?? 0;
            return result;
          }, {})
        );
        setTeamCalendar(Array.isArray(calendar) ? calendar : []);
        setTeamFiles(Array.isArray(files) ? files : []);
        setTeamTags(Array.isArray(tags) ? tags : []);
      } catch (error) {
        if (active) {
          setFeedback({ tone: "danger", text: error.message || "Failed to load team workspace." });
        }
      } finally {
        if (active) {
          setLoadingWorkspace(false);
        }
      }
    }

    if (token) {
      loadWorkspace();
    }

    return () => {
      active = false;
    };
  }, [request, selectedTeamId, token, reloadKey]);

  async function handleCreateTeam(event) {
    event.preventDefault();

    try {
      const members = createTeamMembers
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

      const data = await request("/api/team", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: createTeamName.trim(), members }),
      });

      setCreateTeamName("");
      setCreateTeamMembers("");
      setSelectedTeamId(getId(data.team?._id));
      setFeedback({ tone: "success", text: "Team created." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to create team." });
    }
  }

  async function handleInviteMember(event) {
    event.preventDefault();

    if (!selectedTeamId || !inviteEmail.trim()) {
      return;
    }

    try {
      const lookup = await request(`/api/find-user?email=${encodeURIComponent(inviteEmail.trim())}`);
      const memberId = getId(lookup?.user?._id);

      await request(`/api/team/${selectedTeamId}/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ memberId }),
      });

      setInviteEmail("");
      setFeedback({ tone: "success", text: "Member added to team." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to add member." });
    }
  }

  async function handleRemoveMember(memberId) {
    try {
      await request(`/api/team/${selectedTeamId}/remove`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ memberId }),
      });

      setFeedback({ tone: "success", text: "Member removed." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to remove member." });
    }
  }

  async function handleRoleChange(memberId, role) {
    try {
      await request(`/api/team/${selectedTeamId}/role`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ memberId, role }),
      });

      setFeedback({ tone: "success", text: "Role updated." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to update role." });
    }
  }

  async function handleSendMessage(event) {
    event.preventDefault();

    if (!chatInput.trim() || !selectedTeamId) {
      return;
    }

    setSendingMessage(true);

    try {
      await request(`/api/team/${selectedTeamId}/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: chatInput.trim() }),
      });

      setChatInput("");
      setFeedback({ tone: "success", text: "Message sent." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to send message." });
    } finally {
      setSendingMessage(false);
    }
  }

  async function handleReaction(messageId, emoji) {
    try {
      await request(`/api/team/${selectedTeamId}/message/${messageId}/reaction`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emoji }),
      });

      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to react to message." });
    }
  }

  async function handlePinMessage(messageId, pinned) {
    try {
      await request(`/api/team/${selectedTeamId}/pin-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messageId, pinned }),
      });

      setFeedback({ tone: "success", text: pinned ? "Message pinned." : "Message unpinned." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to update pinned message." });
    }
  }

  async function handleCreateGoal(event) {
    event.preventDefault();

    try {
      await request(`/api/team/${selectedTeamId}/goals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: goalForm.title.trim(),
          description: goalForm.description.trim(),
          target: Number(goalForm.target),
        }),
      });

      setGoalForm({ title: "", description: "", target: "" });
      setFeedback({ tone: "success", text: "Goal created." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to create goal." });
    }
  }

  async function handleGoalProgress(index) {
    try {
      await request(`/api/team/${selectedTeamId}/goals/${index}/progress`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ progress: Number(goalProgressDrafts[index] || 0) }),
      });

      setFeedback({ tone: "success", text: "Goal progress updated." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to update goal." });
    }
  }

  async function handleCreateCalendarEvent(event) {
    event.preventDefault();

    try {
      await request(`/api/team/${selectedTeamId}/calendar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(calendarForm),
      });

      setCalendarForm({ title: "", description: "", start: "", end: "", type: "event" });
      setFeedback({ tone: "success", text: "Calendar event created." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to create calendar event." });
    }
  }

  async function handleDeleteCalendarEvent(index) {
    try {
      await request(`/api/team/${selectedTeamId}/calendar/${index}`, {
        method: "DELETE",
      });

      setFeedback({ tone: "success", text: "Calendar event deleted." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to delete calendar event." });
    }
  }

  async function handleCreateTag(event) {
    event.preventDefault();

    try {
      await request(`/api/team/${selectedTeamId}/tags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: tagForm.name.trim(), color: tagForm.color }),
      });

      setTagForm({ name: "", color: "#0ea5e9" });
      setFeedback({ tone: "success", text: "Custom tag created." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to create tag." });
    }
  }

  async function handleDeleteTag(index) {
    try {
      await request(`/api/team/${selectedTeamId}/tags/${index}`, {
        method: "DELETE",
      });

      setFeedback({ tone: "success", text: "Custom tag deleted." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to delete tag." });
    }
  }

  async function handleUploadFile(event) {
    event.preventDefault();

    if (!uploadFile) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", uploadFile);

      await request(`/api/team/${selectedTeamId}/upload`, {
        method: "POST",
        body: formData,
      });

      setUploadFile(null);
      setFeedback({ tone: "success", text: "File uploaded." });
      setReloadKey((value) => value + 1);
    } catch (error) {
      setFeedback({ tone: "danger", text: error.message || "Failed to upload file." });
    }
  }

  const teamMemberRows = selectedTeam
    ? [
        { user: selectedTeam.owner, role: "owner", isOwner: true },
        ...(selectedTeam.members || []).map((member) => ({
          user: member.user,
          role: member.role,
          isOwner: false,
        })),
      ]
    : [];

  const visiblePinnedMessages = pinnedMessages.slice(0, 3);
  const visibleMessages = teamMessages.slice(-10).reverse();
  const visibleTrades = teamTrades.slice(0, 6);
  const visibleActivity = activityFeed.slice(0, 8);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.16),_transparent_22%),radial-gradient(circle_at_80%_20%,_rgba(251,191,36,0.18),_transparent_18%),linear-gradient(135deg,_#0f172a,_#111827_58%,_#1d4ed8)] text-white dark:border-gray-700">
        <SectionHeader
          eyebrow="Team Workspace"
          title="Run shared trading operations with more structure"
          description="Bring members, messages, performance, goals, and operating context into one premium workspace instead of scattering them across chats and spreadsheets."
          action={<StatusPill tone="warning">{isEnterprise ? "Enterprise access" : "Team access"}</StatusPill>}
        />
        <div className="mt-6 grid gap-4 md:grid-cols-4">
          <MetricCard label="Teams" value={String(teams.length)} helper="Workspaces linked to this account." className="border-white/10 bg-white/10" tone="text-white" />
          <MetricCard label="Role" value={myRole || "none"} helper="Your access level in the selected team." className="border-white/10 bg-white/10" tone="text-white" />
          <MetricCard label="Members" value={selectedTeam ? String(teamMemberRows.length) : "0"} helper="Owner plus active members." className="border-white/10 bg-white/10" tone="text-white" />
          <MetricCard label="Messages" value={String(teamMessages.length)} helper="Visible team chat entries." className="border-white/10 bg-white/10" tone="text-white" />
        </div>
      </SurfaceCard>

      {feedback.text ? (
        <SurfaceCard>
          <StatusPill tone={feedback.tone === "danger" ? "danger" : feedback.tone === "success" ? "success" : "neutral"}>{feedback.text}</StatusPill>
        </SurfaceCard>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <SurfaceCard>
          <SectionHeader
            eyebrow="Workspace Control"
            title="Select or create a team"
            description="The old TeamManagement file was missing entirely, so this page now restores team access, creation, and membership workflows in one place."
          />
          <div className="mt-6 space-y-5">
            <SelectField label="Selected team" value={selectedTeamId} onChange={(event) => setSelectedTeamId(event.target.value)}>
              {!teams.length ? <option value="">No teams yet</option> : null}
              {teams.map((team) => (
                <option key={getId(team._id)} value={getId(team._id)}>{team.name}</option>
              ))}
            </SelectField>

            <form onSubmit={handleCreateTeam} className="space-y-4">
              <Field label="New team name" value={createTeamName} onChange={(event) => setCreateTeamName(event.target.value)} placeholder="London Desk, Alpha Circle" required />
              <Field label="Invite members by email" value={createTeamMembers} onChange={(event) => setCreateTeamMembers(event.target.value)} placeholder="one@email.com, two@email.com" />
              <Button type="submit" variant="primary" disabled={!createTeamName.trim()}>
                <Plus className="h-4 w-4" />
                Create Team
              </Button>
            </form>

            {selectedTeam ? (
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-gray-700 dark:bg-gray-900">
                <div className="flex items-center gap-3">
                  {myRole === "owner" ? <Crown className="h-5 w-5 text-amber-600 dark:text-amber-300" /> : <Shield className="h-5 w-5 text-sky-600 dark:text-sky-300" />}
                  <div>
                    <p className="text-sm font-semibold text-slate-950 dark:text-white">{selectedTeam.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Owner: {getUserLabel(selectedTeam.owner)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                {loadingTeams ? "Loading teams..." : "No team linked yet. Create one here to start the shared workspace."}
              </div>
            )}
          </div>
        </SurfaceCard>

        <SurfaceCard>
          <SectionHeader
            eyebrow="Workspace Metrics"
            title="Team operating snapshot"
            description="Team trades, review cadence, and collaboration signals should be visible before you scroll into raw details."
          />
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="Team P&L"
              value={teamAnalytics ? <DualCurrencyValue usdValue={teamAnalytics.totalProfit} usdToNgnRate={usdToNgnRate} primaryClassName="text-current" secondaryClassName="text-xs text-slate-500 dark:text-slate-400" /> : "-"}
              helper="USD primary with naira conversion underneath."
            />
            <MetricCard label="Win rate" value={teamAnalytics?.winRate ? `${teamAnalytics.winRate}%` : "-"} helper="Current team win rate." />
            <MetricCard label="Trade count" value={teamAnalytics?.tradeCount ? String(teamAnalytics.tradeCount) : "0"} helper="All team trades in analytics scope." />
            <MetricCard
              label="Max drawdown"
              value={teamAnalytics ? <DualCurrencyValue usdValue={teamAnalytics.maxDrawdown} usdToNgnRate={usdToNgnRate} primaryClassName="text-current" secondaryClassName="text-xs text-slate-500 dark:text-slate-400" /> : "-"}
              helper="Peak-to-trough drawdown in USD with NGN underneath."
            />
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <MetricCard label="Pinned messages" value={String(pinnedMessages.length)} helper="Current pinned announcements." />
            <MetricCard label="Active goals" value={String(teamGoals.filter((goal) => !goal.completed).length)} helper="Goals still in progress." />
          </div>
        </SurfaceCard>
      </div>

      {selectedTeam ? (
        <>
          <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
            <SurfaceCard>
              <SectionHeader eyebrow="Members" title="Access and role management" description="Keep ownership, member visibility, and role changes explicit instead of buried in side effects." />
              <div className="mt-6 space-y-4">
                {teamMemberRows.map((entry) => {
                  const memberId = getId(entry.user);
                  const canEditRole = canManageRoles && !entry.isOwner;
                  const canRemove = canRemoveMembers && !entry.isOwner;

                  return (
                    <div key={memberId || getUserLabel(entry.user)} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-950 dark:text-white">{getUserLabel(entry.user)}</p>
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{getUserEmail(entry.user)}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          {canEditRole ? (
                            <select
                              value={entry.role}
                              onChange={(event) => handleRoleChange(memberId, event.target.value)}
                              className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
                            >
                              <option value="admin">admin</option>
                              <option value="member">member</option>
                              <option value="guest">guest</option>
                            </select>
                          ) : (
                            <StatusPill tone={entry.role === "owner" ? "warning" : entry.role === "admin" ? "brand" : "neutral"}>{entry.role}</StatusPill>
                          )}
                          {canRemove ? (
                            <Button type="button" variant="danger" onClick={() => handleRemoveMember(memberId)}>Remove</Button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={handleInviteMember} className="mt-6 space-y-4">
                <Field label="Add member by email" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} placeholder="user@example.com" />
                <Button type="submit" variant="secondary" disabled={!inviteEmail.trim()}>
                  <Users className="h-4 w-4" />
                  Add Member
                </Button>
              </form>
            </SurfaceCard>

            <SurfaceCard>
              <SectionHeader eyebrow="Team Chat" title="Pinned announcements and live discussion" description="This replaces the deleted legacy chat view with a cleaner message lane, pin controls, and reactions." />
              {visiblePinnedMessages.length ? (
                <div className="mt-6 space-y-3">
                  {visiblePinnedMessages.map((message) => (
                    <div key={message._id} className="rounded-3xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/10">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-950 dark:text-white">{getUserLabel(message.sender)}</p>
                          <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{message.content}</p>
                          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{formatDateTime(message.createdAt)}</p>
                        </div>
                        {canManageWorkspace ? (
                          <Button type="button" variant="secondary" onClick={() => handlePinMessage(message._id, false)}>
                            <Pin className="h-4 w-4" />
                            Unpin
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="mt-6 space-y-4">
                {visibleMessages.length ? visibleMessages.map((message) => (
                  <div key={message._id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{getUserLabel(message.sender)}</p>
                        <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{message.content}</p>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{formatDateTime(message.createdAt)}</p>
                      </div>
                      {canManageWorkspace && !message.pinned ? (
                          <Button type="button" variant="secondary" onClick={() => handlePinMessage(message._id, true)}>
                            <Pin className="h-4 w-4" />
                          Pin
                        </Button>
                      ) : null}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {Array.from(new Set((message.reactions || []).map((reaction) => reaction.emoji))).map((emoji) => {
                        const count = (message.reactions || []).filter((reaction) => reaction.emoji === emoji).length;
                        return <StatusPill key={`${message._id}-${emoji}`} tone="neutral">{emoji} {count}</StatusPill>;
                      })}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {["👍", "🔥", "🎯", "👏", "💡", "🚀"].map((emoji) => (
                        <button
                          key={`${message._id}-${emoji}-button`}
                          type="button"
                          className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm transition hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                          onClick={() => handleReaction(message._id, emoji)}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )) : (
                  <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                    {loadingWorkspace ? "Loading team messages..." : "No team messages yet."}
                  </div>
                )}
              </div>
              <form onSubmit={handleSendMessage} className="mt-6 space-y-4">
                <TextareaField label="Send to team chat" value={chatInput} onChange={(event) => setChatInput(event.target.value)} rows={4} placeholder="Share a trade insight, operating note, or desk instruction." />
                <Button type="submit" variant="primary" disabled={sendingMessage || !chatInput.trim()}>
                  <Send className="h-4 w-4" />
                  {sendingMessage ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </SurfaceCard>
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
            <SurfaceCard>
              <SectionHeader eyebrow="Performance" title="Leaderboard and recent team trades" description="Shared performance should be visible by member, not hidden behind aggregate statistics only." />
              <div className="mt-6 space-y-4">
                {leaderboard.length ? leaderboard.map((entry, index) => (
                  <div key={getId(entry.user?._id) || `${entry.user?.email}-${index}`} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white dark:bg-white dark:text-slate-900">#{index + 1}</div>
                        <div>
                          <p className="text-sm font-semibold text-slate-950 dark:text-white">{getUserLabel(entry.user)}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{entry.role}</p>
                        </div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-right dark:border-gray-700 dark:bg-gray-800">
                          <p className={`text-sm font-semibold ${Number(entry.totalProfit) >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300"}`}>{formatUsd(entry.totalProfit)}</p>
                          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{formatNaira((Number(entry.totalProfit) || 0) * usdToNgnRate)}</p>
                        </div>
                        <StatusPill tone="brand">{entry.winRate}% win</StatusPill>
                        <StatusPill tone="neutral">{entry.tradeCount} trades</StatusPill>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">No leaderboard data yet.</div>
                )}
              </div>

              <div className="mt-8">
                <SectionHeader eyebrow="Recent Trades" title="Latest executions" description="A quick read on the team ledger without leaving the workspace." />
                <div className="mt-4 space-y-3">
                  {visibleTrades.length ? visibleTrades.map((trade) => (
                    <div key={trade._id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                      {(() => {
                        const displayProfit = getTradeUsdProfit(trade, usdToNgnRate);

                        return (
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-950 dark:text-white">{trade.asset} · {trade.direction}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{formatDateTime(trade.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-semibold ${displayProfit >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300"}`}>{formatUsd(displayProfit)}</p>
                          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{formatNaira(getTradeNgnProfit(trade, usdToNgnRate))}</p>
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                  )) : (
                    <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">No team trades yet.</div>
                  )}
                </div>
              </div>
            </SurfaceCard>

            <SurfaceCard>
              <SectionHeader eyebrow="Activity Feed" title="Recent member actions" description="Messages, joins, removals, and trade events should leave an operational trail." />
              <div className="mt-6 space-y-4">
                {visibleActivity.length ? visibleActivity.map((activity) => (
                  <div key={activity._id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <p className="text-sm font-semibold text-slate-950 dark:text-white">{activity.type.replace(/-/g, " ")}</p>
                    <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{activitySummary(activity)}</p>
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{formatDateTime(activity.createdAt)}</p>
                  </div>
                )) : (
                  <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">No team activity yet.</div>
                )}
              </div>
            </SurfaceCard>
          </div>

          <div className="grid gap-6 xl:grid-cols-3">
            <SurfaceCard>
              <SectionHeader eyebrow="Goals" title="Team challenges and targets" description="Shared goals make team discipline visible and measurable." />
              <form onSubmit={handleCreateGoal} className="mt-6 space-y-4">
                <Field label="Goal title" value={goalForm.title} onChange={(event) => setGoalForm((current) => ({ ...current, title: event.target.value }))} placeholder="Reach 60% win rate" />
                <TextareaField label="Description" value={goalForm.description} onChange={(event) => setGoalForm((current) => ({ ...current, description: event.target.value }))} rows={3} />
                <Field label="Target" type="number" value={goalForm.target} onChange={(event) => setGoalForm((current) => ({ ...current, target: event.target.value }))} />
                <Button type="submit" variant="secondary" disabled={!canManageWorkspace}>
                  <Target className="h-4 w-4" />
                  Add Goal
                </Button>
              </form>
              <div className="mt-6 space-y-3">
                {teamGoals.length ? teamGoals.map((goal, index) => (
                  <div key={`${goal.title}-${index}`} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{goal.title}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Target: {goal.target}</p>
                      </div>
                      <StatusPill tone={goal.completed ? "success" : "warning"}>{goal.completed ? "Completed" : "Active"}</StatusPill>
                    </div>
                    {goal.description ? <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{goal.description}</p> : null}
                    <div className="mt-4 flex gap-2">
                      <input
                        type="number"
                        value={goalProgressDrafts[index] ?? goal.progress ?? 0}
                        onChange={(event) => setGoalProgressDrafts((current) => ({ ...current, [index]: event.target.value }))}
                        className="w-full rounded-full border border-slate-200 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
                      />
                      <Button type="button" variant="brand" onClick={() => handleGoalProgress(index)}>
                        <Flag className="h-4 w-4" />
                        Update
                      </Button>
                    </div>
                  </div>
                )) : <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">No team goals yet.</div>}
              </div>
            </SurfaceCard>

            <SurfaceCard>
              <SectionHeader eyebrow="Calendar" title="Shared events and deadlines" description="Trading teams need a visible cadence for reviews, meetings, and deadlines." />
              <form onSubmit={handleCreateCalendarEvent} className="mt-6 space-y-4">
                <Field label="Title" value={calendarForm.title} onChange={(event) => setCalendarForm((current) => ({ ...current, title: event.target.value }))} />
                <Field label="Description" value={calendarForm.description} onChange={(event) => setCalendarForm((current) => ({ ...current, description: event.target.value }))} />
                <Field label="Start" type="datetime-local" value={calendarForm.start} onChange={(event) => setCalendarForm((current) => ({ ...current, start: event.target.value }))} />
                <Field label="End" type="datetime-local" value={calendarForm.end} onChange={(event) => setCalendarForm((current) => ({ ...current, end: event.target.value }))} />
                <SelectField label="Type" value={calendarForm.type} onChange={(event) => setCalendarForm((current) => ({ ...current, type: event.target.value }))}>
                  <option value="event">event</option>
                  <option value="deadline">deadline</option>
                  <option value="meeting">meeting</option>
                </SelectField>
                <Button type="submit" variant="secondary" disabled={!canManageWorkspace}>
                  <CalendarDays className="h-4 w-4" />
                  Add Event
                </Button>
              </form>
              <div className="mt-6 space-y-3">
                {teamCalendar.length ? teamCalendar.map((item, index) => (
                  <div key={`${item.title}-${index}`} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{item.title}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{item.type} · {formatDateTime(item.start)}</p>
                      </div>
                      {canManageWorkspace ? <Button type="button" variant="danger" onClick={() => handleDeleteCalendarEvent(index)}>Delete</Button> : null}
                    </div>
                    {item.description ? <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{item.description}</p> : null}
                  </div>
                )) : <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">No calendar items yet.</div>}
              </div>
            </SurfaceCard>

            <SurfaceCard>
              <SectionHeader eyebrow="Files and Tags" title="Shared references and taxonomy" description="Keep team uploads and custom labels inside the same operating layer." />
              <form onSubmit={handleUploadFile} className="mt-6 space-y-4">
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">Upload file</span>
                  <input type="file" onChange={(event) => setUploadFile(event.target.files?.[0] || null)} className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-900" />
                </label>
                <Button type="submit" variant="secondary" disabled={!uploadFile}>
                  <Upload className="h-4 w-4" />
                  Upload
                </Button>
              </form>

              <form onSubmit={handleCreateTag} className="mt-6 space-y-4">
                <Field label="Tag name" value={tagForm.name} onChange={(event) => setTagForm((current) => ({ ...current, name: event.target.value }))} placeholder="A+ setup" />
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">Color</span>
                  <input type="color" value={tagForm.color} onChange={(event) => setTagForm((current) => ({ ...current, color: event.target.value }))} className="h-11 w-full rounded-2xl border border-slate-200 bg-white px-2 dark:border-gray-700 dark:bg-gray-900" />
                </label>
                <Button type="submit" variant="secondary" disabled={!canManageWorkspace}>
                  <Tags className="h-4 w-4" />
                  Add Tag
                </Button>
              </form>

              <div className="mt-6 space-y-3">
                {teamFiles.length ? teamFiles.map((file) => (
                  <div key={`${file.filename}-${file.originalname}`} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{file.originalname}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Uploaded {formatDateTime(file.uploadedAt)}</p>
                      </div>
                      {file.cloudinaryUrl ? (
                        <a href={file.cloudinaryUrl} target="_blank" rel="noreferrer" className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100">
                          <Files className="mr-2 inline h-4 w-4" />
                          Open
                        </a>
                      ) : (
                        <StatusPill tone="neutral">Stored locally</StatusPill>
                      )}
                    </div>
                  </div>
                )) : null}

                {teamTags.length ? teamTags.map((tag, index) => (
                  <div key={`${tag.name}-${index}`} className="flex items-center justify-between rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-center gap-3">
                      <span className="h-4 w-4 rounded-full" style={{ backgroundColor: tag.color || "#94a3b8" }} />
                      <p className="text-sm font-semibold text-slate-950 dark:text-white">{tag.name}</p>
                    </div>
                    {canManageWorkspace ? <Button type="button" variant="danger" onClick={() => handleDeleteTag(index)}>Delete</Button> : null}
                  </div>
                )) : null}

                {!teamFiles.length && !teamTags.length ? (
                  <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">No files or custom tags yet.</div>
                ) : null}
              </div>
            </SurfaceCard>
          </div>
        </>
      ) : null}

      {!selectedTeam && !loadingTeams ? (
        <SurfaceCard>
          <SectionHeader eyebrow="Next Step" title="Create the first team workspace" description="Once a team exists, this page unlocks members, activity, chat, leaderboard, goals, calendar, files, and custom tags from the same surface." />
          <div className="mt-6 flex flex-wrap gap-3">
            <StatusPill tone="brand">Members and roles</StatusPill>
            <StatusPill tone="warning">Pinned chat and reactions</StatusPill>
            <StatusPill tone="success">Leaderboard and analytics</StatusPill>
            <StatusPill tone="neutral">Goals, calendar, files, and tags</StatusPill>
          </div>
        </SurfaceCard>
      ) : null}

      {loadingWorkspace ? (
        <SurfaceCard>
          <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
            <TrendingUp className="h-4 w-4" />
            Loading team workspace...
          </div>
        </SurfaceCard>
      ) : null}
    </div>
  );
}

export default TeamManagement;